import { useState, useMemo } from 'react';
import { prefixClass } from 'lib/utils';
import IconButton from '../../../../../../Common/IconButton';
import { Dropdown, type DropdownItemConfig } from '../../../../../../Common/Dropdown';
import {
  FilterModalContent,
  type DueDatePreset,
  type DateRange,
  type RequestTypeFilter,
  type AssigneeFilterOption,
} from '../FilterModal';
import type { WorkState } from '../../../../components/RequestCard';
import type { QueueView } from '@hone-automation/common';
import { type DateField, dateFieldLabels } from '../../../../utils/dateGrouping';
import {
  IconMention,
  IconAttachment,
  IconAssignmentNew,
  IconFilter,
  IconSortTimeUp,
  IconSortTimeDown,
  IconCheck,
  IconChevron,
  IconChevronDown,
  IconDirUp,
  IconDirDown,
  IconValidateNew,
  IconFolder,
} from 'components/Icons';
import './FiltersRow.scss';

/** Labels for WorkState values */
const workStateLabels: Record<WorkState, string> = {
  backlog: 'Backlog',
  todo: 'Not Started',
  'in-progress': 'In Progress',
  blocked: 'Blocker',
  closed: 'Completed',
};

/** Labels for DueDatePreset values */
const dueDateLabels: Record<NonNullable<DueDatePreset>, string> = {
  overdue: 'Overdue',
  today: 'Due Today',
  'this-week': 'Due This Week',
  'this-month': 'Due This Month',
  'no-due-date': 'No Due Date',
};

export interface FiltersRowProps {
  sortAscending: boolean;
  onSortToggle: () => void;

  // Date field for grouping/sorting
  dateField: DateField;
  onDateFieldChange: (field: DateField) => void;

  // Toolbar filters
  mentionFilterActive: boolean;
  attachmentFilterActive: boolean;
  assignedFilterActive: boolean;
  workStateFilter: Set<WorkState>;
  dueDateFilter: DueDatePreset;
  dateRangeFilter: DateRange;
  requestTypeFilter: Set<RequestTypeFilter>;
  onMentionFilterToggle: () => void;
  onAttachmentFilterToggle: () => void;
  onAssignedFilterToggle: () => void;
  onWorkStateFilterChange: (states: Set<WorkState>) => void;
  onDueDateFilterChange: (preset: DueDatePreset) => void;
  onDateRangeFilterChange: (range: DateRange) => void;
  onRequestTypeFilterChange: (types: Set<RequestTypeFilter>) => void;

  // Counts for visibility (hide button if count is 0)
  mentionCount: number;
  attachmentCount: number;
  assignedCount: number;

  // WorkState counts for dropdown items
  workStateCounts: Record<WorkState, number>;

  // Request type counts for dropdown items
  requestTypeCounts?: Record<RequestTypeFilter, number>;

  // Assignee filter
  assigneeFilter: string | null;
  onAssigneeFilterChange: (userId: string | null) => void;
  assigneeOptions: AssigneeFilterOption[];

  // Current active view (to hide filter in Blockers view)
  activeView?: QueueView;

  // Groups filter
  groupCount: number;
  groupFilterActive: boolean;
  onGroupFilterToggle: () => void;
}

/**
 * FiltersRow - Second row of the browser toolbar.
 * Contains quick filters on the left and sort controls on the right.
 */
export const FiltersRow = ({
  sortAscending,
  onSortToggle,
  dateField,
  onDateFieldChange,
  mentionFilterActive,
  attachmentFilterActive,
  assignedFilterActive,
  workStateFilter,
  dueDateFilter,
  dateRangeFilter,
  requestTypeFilter,
  onMentionFilterToggle,
  onAttachmentFilterToggle,
  onAssignedFilterToggle,
  onWorkStateFilterChange,
  onDueDateFilterChange,
  onDateRangeFilterChange,
  onRequestTypeFilterChange,
  mentionCount,
  attachmentCount,
  assignedCount,
  workStateCounts,
  requestTypeCounts,
  assigneeFilter,
  onAssigneeFilterChange,
  assigneeOptions,
  activeView,
  groupCount,
  groupFilterActive,
  onGroupFilterToggle,
}: FiltersRowProps) => {
  const prefix = prefixClass('ksq2-browser-toolbar');
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [dateFieldOpen, setDateFieldOpen] = useState(false);

  // Build dropdown items for date field selector
  const dateFieldItems: DropdownItemConfig[] = useMemo(
    () =>
      (Object.keys(dateFieldLabels) as DateField[]).map(field => ({
        id: field,
        label: dateFieldLabels[field],
        icon: field === dateField ? <IconValidateNew /> : undefined,
        onClick: () => onDateFieldChange(field),
      })),
    [dateField, onDateFieldChange]
  );

  // Check if any advanced filter is active (for icon highlight)
  const hasActiveAdvancedFilter =
    workStateFilter.size > 0 ||
    dueDateFilter !== null ||
    dateRangeFilter.from !== null ||
    dateRangeFilter.to !== null ||
    requestTypeFilter.size > 0 ||
    assigneeFilter !== null;

  // Don't show advanced filter in Blockers view (already filtered)
  const showAdvancedFilter = activeView !== 'Blockers';

  // Handle clear all filters
  const handleClearAll = () => {
    onWorkStateFilterChange(new Set());
    onDueDateFilterChange(null);
    onDateRangeFilterChange({ from: null, to: null });
    onRequestTypeFilterChange(new Set());
    onAssigneeFilterChange(null);
  };

  /** Labels for RequestTypeFilter values */
  const requestTypeLabels: Record<RequestTypeFilter, string> = {
    email: 'Email',
    transaction: 'Transaction',
    'flagged-cell': 'Flagged Cell',
    'custom-request': 'Custom Request',
  };

  // Build dynamic tooltip for advanced filters
  const buildAdvancedFilterTooltip = (): string => {
    if (!hasActiveAdvancedFilter) {
      return 'Advanced filters';
    }

    const parts: string[] = [];

    // Work state filters
    if (workStateFilter.size > 0) {
      const states = Array.from(workStateFilter).map(s => workStateLabels[s]);
      parts.push(`Status: ${states.join(', ')}`);
    }

    // Due date filter
    if (dueDateFilter) {
      parts.push(`Due: ${dueDateLabels[dueDateFilter]}`);
    }

    // Date range filter
    if (dateRangeFilter.from || dateRangeFilter.to) {
      const fromStr = dateRangeFilter.from ? dateRangeFilter.from.toLocaleDateString() : '...';
      const toStr = dateRangeFilter.to ? dateRangeFilter.to.toLocaleDateString() : '...';
      parts.push(`Range: ${fromStr} - ${toStr}`);
    }

    // Request type filter
    if (requestTypeFilter.size > 0) {
      const types = Array.from(requestTypeFilter).map(t => requestTypeLabels[t]);
      parts.push(`Type: ${types.join(', ')}`);
    }

    // Assignee filter
    if (assigneeFilter) {
      const assignee = assigneeOptions.find(o => o.userId === assigneeFilter);
      parts.push(`Assignee: ${assignee?.name || 'Unknown'}`);
    }

    return parts.join(' • ');
  };

  return (
    <div className={prefix('__filters-row')}>
      {/* Left: Quick filters */}
      <div className={prefix('__filters')}>
        {groupCount > 0 && activeView !== 'Groups' && (
          <IconButton
            icon={<IconFolder />}
            size="small"
            rounded
            type="switch"
            selected={groupFilterActive}
            tooltip={groupFilterActive ? `Filtering by groups (${groupCount})` : `Filter by groups (${groupCount})`}
            onClick={onGroupFilterToggle}
            className="ksq2-filter-btn"
          />
        )}
        {attachmentCount > 0 && (
          <IconButton
            icon={<IconAttachment />}
            size="small"
            rounded
            type="switch"
            selected={attachmentFilterActive}
            tooltip={
              attachmentFilterActive
                ? `Filtering by attachments (${attachmentCount})`
                : `Filter by attachments (${attachmentCount})`
            }
            onClick={onAttachmentFilterToggle}
            className="ksq2-filter-btn"
          />
        )}
        {mentionCount > 0 && (
          <IconButton
            icon={<IconMention />}
            size="small"
            rounded
            type="switch"
            selected={mentionFilterActive}
            tooltip={
              mentionFilterActive ? `Filtering by mentions (${mentionCount})` : `Filter by mentions (${mentionCount})`
            }
            onClick={onMentionFilterToggle}
            className="ksq2-filter-btn"
          />
        )}
        {assignedCount > 0 && activeView !== 'Me' && (
          <IconButton
            icon={<IconAssignmentNew />}
            size="small"
            rounded
            type="switch"
            selected={assignedFilterActive}
            tooltip={
              assignedFilterActive
                ? `Filtering by assigned (${assignedCount})`
                : `Filter by assigned (${assignedCount})`
            }
            onClick={onAssignedFilterToggle}
            className="ksq2-filter-btn"
          />
        )}
        {showAdvancedFilter && (
          <Dropdown
            trigger={
              <IconButton
                icon={<IconFilter />}
                size="small"
                type="switch"
                rounded
                selected={hasActiveAdvancedFilter}
                tooltip={buildAdvancedFilterTooltip()}
                onClick={() => {}}
                className="ksq2-filter-btn"
              />
            }
            position="bottom"
            alignment="start"
            isOpen={filterModalOpen}
            onOpenChange={setFilterModalOpen}
            closeOnItemClick={false}
            className="ksq2-advanced-filter-dropdown"
          >
            <FilterModalContent
              workStateFilter={workStateFilter}
              onWorkStateFilterChange={onWorkStateFilterChange}
              workStateCounts={workStateCounts}
              dueDateFilter={dueDateFilter}
              onDueDateFilterChange={onDueDateFilterChange}
              dateRangeFilter={dateRangeFilter}
              onDateRangeFilterChange={onDateRangeFilterChange}
              requestTypeFilter={requestTypeFilter}
              onRequestTypeFilterChange={onRequestTypeFilterChange}
              requestTypeCounts={requestTypeCounts}
              assigneeFilter={assigneeFilter}
              onAssigneeFilterChange={onAssigneeFilterChange}
              assigneeOptions={assigneeOptions}
              onClearAll={handleClearAll}
              onClose={() => setFilterModalOpen(false)}
            />
          </Dropdown>
        )}
      </div>

      {/* Right: Sort controls */}
      <div className={prefix('__sort-controls')}>
        <Dropdown
          trigger={
            <button className={prefix('__date-field-btn')} title="Change date field for grouping">
              {dateFieldLabels[dateField]}
              <span className={prefix('__dropdown-arrow')}>
                <IconChevronDown />
              </span>
            </button>
          }
          items={dateFieldItems}
          position="bottom"
          alignment="start"
          isOpen={dateFieldOpen}
          onOpenChange={setDateFieldOpen}
          className="ksq2-date-field-dropdown"
          tooltip="Change date field for grouping"
        />
        <IconButton
          icon={<IconDirDown />}
          iconSelected={<IconDirUp />}
          type="switch"
          selected={sortAscending}
          size="medium"
          tooltip={sortAscending ? 'Show newest first' : 'Show oldest first'}
          onClick={onSortToggle}
        />
      </div>
    </div>
  );
};
